import {
  ALL,
  ALLOCATED,
  DEFAULT_LABEL_CHANGES,
  UNALLOCATED,
} from "../../consts/routeDetails";
import { MATRIX_DAY_MISSING, MATRIX_MODES } from "../../consts/strings";
import {
  setUserSessionExpired,
  setLocksRefreshRequired,
  setLoading,
  applyEdit,
  cancelAllEdits,
  cancelEdit,
  copyPlan,
  createPlan,
  editPostcodesOverrides,
  generateModalNewOverrides,
  updateModalNewOverrides,
  applyOverridesEdit,
  cancelOverridesEdit,
  setRouteDayLocks,
  lockRouteDay,
  unlockRouteDay,
  lockDriverDay,
  unlockDriverDay,
  forceUnlockDrivers,
  editDriver,
  updateDriverEdit,
  processDriverEdit,
  saveDriverDays,
  applyDriverEdit,
  closeDriverEdit,
  revertDriverEdit,
  deletePlan,
  editCollectionTimes,
  editPlan,
  editRoute,
  fetchDepotRouteMatrix,
  fetchMatrixProfiles,
  fetchOverlays,
  fetchVolumeData,
  fetchSimulationPercentages,
  lockPlan,
  setWaveEditMode,
  moveWave,
  applyWavesChanges,
  cancelWavesChanges,
  optimiseDepotRoutes,
  optimiseRouteEditImpact,
  resetMatrix,
  savePlan,
  setMatrixModeThunk,
  setMatrixProfileOptions,
  setRoutePostcodes,
  processRouteEditAction,
  processCollectionEditAction,
  processLocationEditAction,
  setNoCoordinatesLocation,
  clearRouteDeliveryAreas,
  clearRouteCollectionAreas,
  clearRouteOverrideAreas,
  clearRouteLocations,
  unlockPlan,
  validateProfile,
  forceUnlockDriversDay,
  fetchRobotSuppliers,
  refreshTemplateLocks,
  editPostcodesPickup,
  applyPickupEdit,
  cancelPickupEdit,
  updatePickupModal,
  clearRoutePickupAreas,
} from "./thunkActions";

import { createSlice } from "@reduxjs/toolkit";
import { getWavesFromRoutes } from "../../utils/waves";
import { removeVolumeOverlayFromRoutes } from "../../utils/routes";

const initialState = {
  action: null,
  changedOuter: null,
  collectionAreas: null,
  editCollectionAreas: null,
  collectionsView: false,
  routesByOuter: null,
  routesByOuterCopy: null, // this is set before starting editing a route. Needed to assess which routes are impacted
  routesTimes: null,
  routesTimesCopy: null, //this is set before starting editing a route. Needed to asses which routes are impacted
  cutOffTimes: null,
  datedProfiles: [],
  dayProfiles: [],
  heatmapView: false,
  loading: false,
  checksum: null,
  sessionChecksum: null,
  userSessionExpired: false,
  locksRefreshRequired: false,
  lockedByUserCode: null,
  lockedByUserName: null,
  lockedDatetime: null,
  lockedByYou: false,
  lockedInDifferentBrowser: false,
  matrixMode: MATRIX_MODES.NONE_SELECTED,
  message: MATRIX_DAY_MISSING,
  multiData: {},
  multiPostcodes: null,
  overlays: null,
  postcodeToRoute: null,
  profile: null,
  routeConfigEdit: null,
  routeConfigEditCopy: null, //this is set before starting editing a wave/route. Needed for reverting changes made to a waves/routes
  redirectConfig: null,
  redirectConfigCopy: null, //this is set before starting editing a wave/route. Needed for reverting changes made to a waves/routes
  driverEdit: null,
  driverEditCopy: null,
  driverProfileLocks: {},
  routeDayLocks: {},
  driverDayLocks: {},
  driversImpacted: [],
  drivers: null,
  robotSuppliers: null,
  pickupPartners: null,
  vehicles: null,
  routeEdit: null,
  routeLabels: null,
  routeMatrix: null,
  routePostcodes: { ...DEFAULT_LABEL_CHANGES },
  routes: [],
  editRoutes: null,
  editRoutesCopy: null, //this is set before starting editing a route. Needed for reverting changes made to a single route
  routesImpacted: {},
  routesModified: {},
  routesModifiedCopy: {}, //this is set before starting editing a route. Needed to assess which route was already modified before edit began
  routeView: ALL,
  scheduledCollections: [],
  selectedCollection: null,
  selectedOverlay: null,
  templates: [],
  type: null,
  volumeData: null,
  simulationPercentages: [],
  depotSections: null,
  multiRoutesPopupLocation: null,
  optimiseResultsOpen: false,
  depotRoutesOptimised: false,
  depotRoutesOptimisedCopy: false, //this is set before starting wave editing. Needed for reverting changes made to a waves route
  waves: [],
  waveEditMode: false,
  overrideAreas: null,
  editOverrideAreas: null,
  editModalOverrides: null,
  pickupAreas: null,
  editPickupAreas: null,
  pickupModalConfig: null,
  locationConfig: null,
  editLocationConfig: null,
  noCoordinatesLocation: null,
};

const processTemplates = (matrixProfile) => {
  const dayProfiles = [];
  const datedProfiles = [];
  const templates = [];
  matrixProfile.forEach((profile) => {
    if (
      !isNaN(profile.day) &&
      profile.defaultActiveTemplate &&
      parseInt(profile.day, 10) >= 0 &&
      parseInt(profile.day, 10) <= 6
    ) {
      dayProfiles.push(profile);
    } else {
      profile.activeDate
        ? datedProfiles.push(profile)
        : templates.push(profile);
    }
  });
  dayProfiles.sort((a, b) => {
    if (a.day === "0") {
      return 1;
    }
    if (b.day === "0") {
      return -1;
    }
    return parseInt(a.day) - parseInt(b.day);
  });
  datedProfiles.sort((a, b) => new Date(a.activeDate) - new Date(b.activeDate));
  templates.sort((a, b) => a.templateName.localeCompare(b.templateName));
  return {
    dayProfiles,
    datedProfiles,
    templates,
  };
};

export const matrixProfileSlice = createSlice({
  name: "matrixProfile",
  initialState,
  reducers: {
    setRouteView: (state, action) => {
      if (![ALL, UNALLOCATED, ALLOCATED].includes(action.payload)) {
        return;
      }
      state.routeView = action.payload;
    },
    setSelectedOverlay: (state, action) => {
      state.selectedOverlay = action.payload;
      state.volumeData = null;
      if (!state.profile || !state.type) {
        state.heatmapView = true;
      }
      if (!action.payload && state.routes) {
        state.routes = removeVolumeOverlayFromRoutes(state.routes);
      }
    },
    setHeatmapView: (state, action) => {
      state.heatmapView = action.payload;
    },
    setCollectionView: (state, action) => {
      state.collectionsView = action.payload;
      if (!state.collectionsView) {
        state.selectedCollection = null;
      }
    },
    setSelectedCollection: (state, action) => {
      state.selectedCollection = action.payload;
    },
    setMultiRoutesPopupLocation: (state, action) => {
      state.multiRoutesPopupLocation = action.payload;
    },
    setOptimiseResultsOpen: (state, action) => {
      state.optimiseResultsOpen = action.payload;
    },
    setMatrixMode: (state, action) => {
      if (!Object.values(MATRIX_MODES).includes(action.payload)) return;
      state.matrixMode = action.payload;
    },
    updateModalEditOverride: (state, action) => {
      const editOverride = action.payload;
      state.editModalOverrides = {
        ...state.editModalOverrides,
        editOverrides: {
          ...state.editModalOverrides.editOverrides,
          [editOverride.overrideUuid]: editOverride,
        },
      };
    },
    addModalDeleteOverride: (state, action) => {
      const deleteOverride = action.payload;
      const editOverrides = {
        ...state.editModalOverrides.editOverrides,
      };
      delete editOverrides[deleteOverride.overrideUuid];
      state.editModalOverrides = {
        newOverrides: state.editModalOverrides.newOverrides,
        editOverrides,
        deletedOverrides: {
          ...state.editModalOverrides.deletedOverrides,
          [deleteOverride.overrideUuid]: deleteOverride,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetMatrix.fulfilled, () => initialState)
      .addCase(setLoading.fulfilled, (state, action) => {
        state.loading = action.payload;
      })
      .addCase(setMatrixProfileOptions.rejected, (state, action) => {
        state.message = action.payload;
      })
      .addCase(setMatrixProfileOptions.fulfilled, (state, action) => {
        if (action.payload) {
          state.message = action.payload.message;
          state[action.payload.key] = action.payload.value;
        } else {
          state.type = null;
          state.profile = null;
        }
      })
      .addCase(fetchDepotRouteMatrix.pending, (state) => {
        state.matrixMode = MATRIX_MODES.NONE_SELECTED;
        state.sessionChecksum = null;
        state.checksum = null;
        state.lockedByUserCode = null;
        state.lockedByUserName = null;
        state.lockedDatetime = null;
        state.lockedByYou = false;
        state.lockedInDifferentBrowser = false;
        state.routeDayLocks = {};
        state.driverDayLocks = {};
        state.driverProfileLocks = {};
        state.routeMatrix = null;
        state.driverEdit = null;
        state.routeEdit = null;
        state.routeConfigEdit = null;
        state.redirectConfig = null;
        state.drivers = null;
        state.editRoutes = null;
        state.routes = null;
        state.loading = true;
        state.depotSections = null;
        state.depotRoutesOptimised = false;
        state.collectionAreas = null;
        state.editCollectionAreas = null;
        state.multiRoutesPopupLocation = null;
        state.overrideAreas = null;
        state.editOverrideAreas = null;
        state.pickupAreas = null;
        state.editPickupAreas = null;
        state.locationConfig = null;
        state.editLocationConfig = null;
      })
      .addCase(fetchDepotRouteMatrix.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchDepotRouteMatrix.fulfilled, (state, action) => {
        const {
          matrixProfile,
          routeConfig,
          redirectConfig,
          pickupPartners,
          vehicles,
          drivers,
          driverProfileLocks,
          driverDayLocks,
          routes,
          routeDayLocks,
          depotCollections,
          routesByOuter,
          routesTimes,
          cutOffTimes,
          sections,
          volumeData,
          collectionAreas,
          overrideAreas,
          locationConfig,
          pickupConfig,
        } = action.payload;
        state.locksRefreshRequired = false;
        state.routeMatrix = { matrixProfile };
        state.routeConfigEdit = routeConfig;
        state.redirectConfig = redirectConfig;
        state.matrixMode = MATRIX_MODES.VIEW;
        state.checksum = matrixProfile.checksum;
        state.sessionChecksum = matrixProfile.sessionChecksum;
        state.lockedByUserCode = matrixProfile.lockedByUserCode;
        state.lockedByUserName = matrixProfile.lockedByUserName;
        state.lockedDatetime = matrixProfile.lockedDatetime;
        state.lockedByYou = matrixProfile.lockedByYou;
        state.lockedInDifferentBrowser = matrixProfile.lockedInDifferentBrowser;
        state.pickupPartners = pickupPartners;
        state.vehicles = vehicles;
        state.drivers = drivers;
        state.driverProfileLocks = driverProfileLocks;
        state.driverDayLocks = driverDayLocks;
        state.routes = routes;
        state.routeDayLocks = routeDayLocks;
        state.scheduledCollections = depotCollections;
        state.routesByOuter = routesByOuter;
        state.routesTimes = routesTimes;
        state.cutOffTimes = cutOffTimes;
        state.loading = false;
        state.driversImpacted = [];
        state.routesImpacted = {};
        state.routesModified = {};
        state.depotSections = sections;
        state.volumeData = volumeData;
        state.waves = getWavesFromRoutes(routes, state.type);
        state.collectionAreas = collectionAreas;
        state.overrideAreas = overrideAreas;
        state.pickupAreas = pickupConfig;
        state.locationConfig = locationConfig;
      })
      .addCase(refreshTemplateLocks.pending, (state) => {
        state.loading = true;
      })
      .addCase(refreshTemplateLocks.fulfilled, (state, action) => {
        state.locksRefreshRequired = false;
        const { driverDayLocks, routeDayLocks } = action.payload;
        state.driverDayLocks = driverDayLocks;
        state.routeDayLocks = routeDayLocks;
      })
      .addCase(optimiseDepotRoutes.pending, (state) => {
        state.loading = true;
      })
      .addCase(optimiseDepotRoutes.fulfilled, (state, action) => {
        if (!!state.editRoutes) {
          state.editRoutes = action.payload;
        } else {
          state.routes = action.payload;
        }
        state.loading = false;
        state.depotRoutesOptimised = true;
      })
      .addCase(fetchMatrixProfiles.fulfilled, (state, action) => {
        const { dayProfiles, datedProfiles, templates } = processTemplates(
          action.payload
        );
        state.dayProfiles = dayProfiles;
        state.datedProfiles = datedProfiles;
        state.templates = templates;
      })
      .addCase(setRoutePostcodes.fulfilled, (state, action) => {
        state.routePostcodes = action.payload.routePostcodes;
        state.multiPostcodes = action.payload.multiPostcodes;
        state.postcodeToRoute = action.payload.postcodeToRoute;
        state.multiData = action.payload.multiData ?? {};
        state.changedOuter = null;
        state.action = null;
      })
      .addCase(editPlan.pending, (state) => {
        state.loading = true;
      })
      .addCase(editPlan.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createPlan.pending, (state) => {
        state.loading = true;
      })
      .addCase(deletePlan.pending, (state) => {
        state.loading = true;
      })
      .addCase(deletePlan.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(copyPlan.pending, (state) => {
        state.loading = true;
      })
      .addCase(copyPlan.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchVolumeData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVolumeData.fulfilled, (state, action) => {
        state.loading = false;
        if (!!state.editRoutes) {
          state.editRoutes = action.payload.routes;
        } else {
          state.routes = action.payload.routes;
        }
        state.routesByOuter = action.payload.routesByOuter;
        state.routesTimes = action.payload.routesTimes;
        state.cutOffTimes = action.payload.cutOffTimes;
        state.volumeData = action.payload.volumeData;
      })
      .addCase(fetchVolumeData.rejected, (state) => {
        state.loading = false;
        state.selectedOverlay = null;
        state.heatmapView = false;
      })
      .addCase(fetchRobotSuppliers.pending, (state) => {
        state.robotSuppliers = null;
      })
      .addCase(fetchRobotSuppliers.fulfilled, (state, action) => {
        state.robotSuppliers = action.payload;
      })
      .addCase(fetchOverlays.pending, (state) => {
        state.overlays = null;
        state.selectedOverlay = null;
      })
      .addCase(fetchOverlays.fulfilled, (state, action) => {
        state.overlays = action.payload;
      })
      .addCase(fetchSimulationPercentages.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSimulationPercentages.fulfilled, (state, action) => {
        state.simulationPercentages = action.payload;

        // Setting loading to false if no simulations are found causes the loading to end too early as another thunkAction is still running
        if (action.payload.length > 0) {
          state.loading = false;
        }
      })
      .addCase(fetchSimulationPercentages.rejected, (state) => {
        state.loading = false;
      })
      .addCase(processCollectionEditAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(processCollectionEditAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(processCollectionEditAction.fulfilled, (state, action) => {
        state.loading = false;
        state.action = action.payload.action;
        state.changedOuter = action.payload.action.outer;
        state.routeEdit = action.payload.routeEdit;
        state.editRoutes = action.payload.routes;
        state.editCollectionAreas = action.payload.collectionAreas;
        state.routesImpacted = action.payload.routesImpacted;
        state.routesByOuter = action.payload.routesByOuter;
      })
      .addCase(processLocationEditAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(processLocationEditAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(processLocationEditAction.fulfilled, (state, action) => {
        state.loading = false;
        state.routeEdit = action.payload.routeEdit;
        state.editRoutes = action.payload.routes;
        state.editLocationConfig = action.payload.locationConfig;
        state.routesImpacted = action.payload.routesImpacted;
        state.volumeData = action.payload.volumeData;
      })
      .addCase(processRouteEditAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(processRouteEditAction.rejected, (state) => {
        state.loading = false;
      })
      .addCase(processRouteEditAction.fulfilled, (state, action) => {
        state.action = action.payload.action;
        state.changedOuter = action.payload.action.outer;
        state.editRoutes = action.payload.routes;
        state.routesImpacted = action.payload.routesImpacted;
        state.routeConfigEdit = action.payload.routeConfigEdit;
        state.redirectConfig = action.payload.redirectConfig;
        state.routeEdit = action.payload.routeEdit;
        state.routesByOuter = action.payload.routesByOuter;
        state.routesTimes = action.payload.routesTimes;
        state.cutOffTimes = action.payload.cutOffTimes;
        if (state.volumeData)
          state.volumeData.overlayDetails = action.payload.overlayDetails;
        state.loading = false;
      })
      .addCase(clearRouteDeliveryAreas.pending, (state) => {
        state.loading = true;
      })
      .addCase(clearRouteDeliveryAreas.rejected, (state) => {
        state.loading = false;
      })
      .addCase(clearRouteDeliveryAreas.fulfilled, (state, action) => {
        state.editRoutes = action.payload.routes;
        state.routeEdit = action.payload.routeEdit;
        state.routesImpacted = action.payload.routesImpacted;
        state.routeConfigEdit = action.payload.routeConfigEdit;
        state.redirectConfig = action.payload.redirectConfig;
        state.routesByOuter = action.payload.routesByOuter;
        state.routesTimes = action.payload.routesTimes;
        state.cutOffTimes = action.payload.cutOffTimes;
        if (state.volumeData)
          state.volumeData = {
            ...state.volumeData,
            overlayDetails: action.payload.overlayDetails,
          };
        state.loading = false;
      })
      .addCase(clearRouteCollectionAreas.pending, (state) => {
        state.loading = true;
      })
      .addCase(clearRouteCollectionAreas.rejected, (state) => {
        state.loading = false;
      })
      .addCase(clearRouteCollectionAreas.fulfilled, (state, action) => {
        state.loading = false;
        state.routeEdit = action.payload.routeEdit;
        state.editRoutes = action.payload.routes;
        state.editCollectionAreas = action.payload.collectionAreas;
        state.routesImpacted = action.payload.routesImpacted;
        state.routesByOuter = action.payload.routesByOuter;
      })
      .addCase(clearRouteLocations.pending, (state) => {
        state.loading = true;
      })
      .addCase(clearRouteLocations.rejected, (state) => {
        state.loading = false;
      })
      .addCase(clearRouteLocations.fulfilled, (state, action) => {
        state.loading = false;
        state.routeEdit = action.payload.routeEdit;
        state.editRoutes = action.payload.routes;
        state.editLocationConfig = action.payload.locationConfig;
        state.volumeData = action.payload.volumeData;
      })
      .addCase(clearRouteOverrideAreas.pending, (state) => {
        state.loading = true;
      })
      .addCase(clearRouteOverrideAreas.rejected, (state) => {
        state.loading = false;
      })
      .addCase(clearRouteOverrideAreas.fulfilled, (state, action) => {
        state.editOverrideAreas = action.payload.overrideAreas;
        state.routeEdit = action.payload.routeEdit;
        state.editRoutes = action.payload.routes;
        state.routesByOuter = action.payload.routesByOuter;
        state.routesTimes = action.payload.routesTimes;
        state.cutOffTimes = action.payload.cutOffTimes;
        state.volumeData = action.payload.volumeData;
        state.loading = false;
      })
      .addCase(clearRoutePickupAreas.pending, (state) => {
        state.loading = true;
      })
      .addCase(clearRoutePickupAreas.rejected, (state) => {
        state.loading = false;
      })
      .addCase(clearRoutePickupAreas.fulfilled, (state, action) => {
        state.loading = false;
        state.routeEdit = action.payload.routeEdit;
        state.editRoutes = action.payload.routes;
        state.editPickupAreas = action.payload.pickupAreas;
        state.volumeData = action.payload.volumeData;
        state.editModalPickupAreas = null;
        state.pickupModalConfig = null;
      })
      .addCase(setUserSessionExpired, (state, action) => {
        state.userSessionExpired = action.payload;
      })
      .addCase(setLocksRefreshRequired, (state, action) => {
        state.locksRefreshRequired = action.payload;
      })
      .addCase(setNoCoordinatesLocation.fulfilled, (state, action) => {
        state.noCoordinatesLocation = action.payload;
      })
      .addCase(lockDriverDay.fulfilled, (state, action) => {
        const {
          driverCode,
          lockedByUserCode,
          lockedByUserName,
          lockedDatetime,
        } = action.payload;
        state.driverDayLocks = {
          ...state.driverDayLocks,
          [driverCode]: {
            checksum: state.driverDayLocks[driverCode].checksum,
            lockedByUserCode,
            lockedByUserName,
            lockedDatetime,
            lockedByYou: true,
            lockedInDifferentBrowser: false,
          },
        };
      })
      .addCase(unlockDriverDay.fulfilled, (state, action) => {
        const { driverCode } = action.payload;
        state.driverDayLocks = {
          ...state.driverDayLocks,
          [driverCode]: {
            checksum: state.driverDayLocks[driverCode].checksum,
            lockedByUserCode: null,
            lockedByUserName: null,
            lockedDatetime: null,
            lockedByYou: false,
            lockedInDifferentBrowser: false,
          },
        };
      })
      .addCase(setRouteDayLocks.fulfilled, (state, action) => {
        state.routeDayLocks = action.payload;
      })
      .addCase(lockRouteDay.fulfilled, (state, action) => {
        const {
          routeCode,
          lockedByUserCode,
          lockedByUserName,
          lockedDatetime,
        } = action.payload;
        state.routeDayLocks = {
          ...state.routeDayLocks,
          [routeCode]: {
            checksum: state.routeDayLocks[routeCode].checksum,
            lockedByUserCode,
            lockedByUserName,
            lockedDatetime,
            lockedByYou: true,
            lockedInDifferentBrowser: false,
          },
        };
      })
      .addCase(unlockRouteDay.fulfilled, (state, action) => {
        const { routeCode } = action.payload;
        state.routeDayLocks = {
          ...state.routeDayLocks,
          [routeCode]: {
            checksum: state.routeDayLocks[routeCode].checksum,
            lockedByUserCode: null,
            lockedByUserName: null,
            lockedDatetime: null,
            lockedByYou: false,
            lockedInDifferentBrowser: false,
          },
        };
      })
      .addCase(forceUnlockDrivers.pending, (state) => {
        state.loading = true;
      })
      .addCase(forceUnlockDrivers.rejected, (state) => {
        state.loading = false;
      })
      .addCase(forceUnlockDriversDay.pending, (state) => {
        state.loading = true;
      })
      .addCase(forceUnlockDriversDay.rejected, (state) => {
        state.loading = false;
      })
      .addCase(editDriver.pending, (state) => {
        state.loading = true;
      })
      .addCase(editDriver.rejected, (state) => {
        state.loading = false;
      })
      .addCase(editDriver.fulfilled, (state, action) => {
        state.loading = false;
        state.matrixMode = action.payload.matrixMode;
        state.driverEdit = action.payload.driverEdit;
        state.driverEditCopy = action.payload.driverEdit;
        state.driversCopy = action.payload.drivers;
        state.editRoutes = action.payload.routes;
        state.editRoutesCopy = action.payload.routes;
      })
      .addCase(updateDriverEdit.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDriverEdit.fulfilled, (state, action) => {
        state.driverEdit = action.payload;
      })
      .addCase(processDriverEdit.pending, (state) => {
        state.loading = true;
      })
      .addCase(processDriverEdit.rejected, (state) => {
        state.loading = false;
      })
      .addCase(processDriverEdit.fulfilled, (state, action) => {
        const {
          routes,
          drivers,
          driversImpacted,
          routesImpacted,
          showOptimiseResults,
          loading,
        } = action.payload;

        state.drivers = drivers;
        state.editRoutes = routes;
        state.optimiseResultsOpen = !!showOptimiseResults;

        if (showOptimiseResults) {
          state.driversImpacted = driversImpacted;
          state.routesImpacted = routesImpacted;
        } else {
          state.driversImpacted = [];
          state.routesImpacted = {};
          state.driversCopy = null;
          state.editRoutesCopy = null;
          state.driverEdit = null;
          state.driverEditCopy = null;
        }

        state.loading = !!loading;
      })
      .addCase(revertDriverEdit.fulfilled, (state) => {
        state.drivers = state.driversCopy;
        state.editRoutes = state.editRoutesCopy;
      })
      .addCase(saveDriverDays.fulfilled, (state, action) => {
        state.routeDayLocks = action.payload.routeDayLocks;
        state.driverDayLocks = action.payload.driverDayLocks;
      })
      .addCase(applyDriverEdit.pending, (state) => {
        state.loading = true;
      })
      .addCase(applyDriverEdit.rejected, (state) => {
        state.loading = false;
      })
      .addCase(applyDriverEdit.fulfilled, (state) => {
        state.loading = false;
        state.driversCopy = null;
        state.editRoutesCopy = null;
        state.driverEdit = null;
        state.driverEditCopy = null;
        state.driversImpacted = [];
        state.routesImpacted = {};
      })
      .addCase(closeDriverEdit.pending, (state) => {
        state.loading = true;
      })
      .addCase(closeDriverEdit.rejected, (state) => {
        state.loading = false;
      })
      .addCase(closeDriverEdit.fulfilled, (state) => {
        state.loading = false;
        state.driversCopy = null;
        state.editRoutesCopy = null;
        state.driverEdit = null;
        state.driverEditCopy = null;
        state.driversImpacted = [];
        state.routesImpacted = {};
      })
      .addCase(editRoute.pending, (state) => {
        state.loading = true;
      })
      .addCase(editRoute.rejected, (state) => {
        state.loading = false;
      })
      .addCase(editRoute.fulfilled, (state, action) => {
        state.matrixMode = MATRIX_MODES.EDIT_ONE_ROUTE;
        state.routesModifiedCopy = action.payload.routesModified;
        state.routeEdit = action.payload.routeEdit;
        state.routeConfigEditCopy = action.payload.routeConfig;
        state.redirectConfigCopy = action.payload.redirectConfig;
        state.editRoutes = action.payload.routes;
        state.editRoutesCopy = action.payload.routes;
        state.editCollectionAreas = action.payload.collectionAreas;
        state.editCollectionAreasCopy = action.payload.collectionAreas;
        state.editOverrideAreas = action.payload.overrideAreas;
        state.editOverrideAreasCopy = action.payload.overrideAreas;
        state.editPickupAreas = action.payload.pickupAreas;
        state.editPickupAreasCopy = action.payload.pickupAreas;
        state.editLocationConfig = action.payload.locationConfig;
        state.editLocationConfigCopy = action.payload.locationConfig;
        state.routesTimesCopy = action.payload.routesTimes;
        state.routesByOuterCopy = action.payload.routesByOuter;
        state.loading = false;
      })
      .addCase(cancelAllEdits.pending, (state) => {
        state.loading = true;
      })
      .addCase(cancelAllEdits.fulfilled, (state) => {
        state.matrixMode = MATRIX_MODES.VIEW;
        state.routesImpacted = {};
        state.routesModified = {};
        state.routesModifiedCopy = {};
        state.routeConfigEditCopy = null;
        state.redirectConfigCopy = null;
        state.waveEditMode = false;
        state.driverEdit = null;
        state.driversCopy = null;
        state.routeEdit = null;
        state.editRoutes = null;
        state.editRoutesCopy = null;
        state.editCollectionAreas = null;
        state.editCollectionAreasCopy = null;
        state.editOverrideAreas = null;
        state.editPickupAreas = null;
        state.routesTimesCopy = null;
        state.routesByOuterCopy = null;
        state.editOverrideAreasCopy = null;
        state.editModalOverrides = null;
        state.editPickupAreasCopy = null;
        state.editModalPickupAreas = null;
        state.editLocationConfig = null;
        state.editLocationConfigCopy = null;
        state.loading = false;
      })
      .addCase(cancelAllEdits.rejected, (state) => {
        state.loading = false;
      })
      .addCase(unlockPlan.pending, (state) => {
        state.loading = true;
      })
      .addCase(unlockPlan.rejected, (state) => {
        state.loading = false;
      })
      .addCase(unlockPlan.fulfilled, (state, action) => {
        const { keepLoading } = action.payload;
        state.matrixMode = MATRIX_MODES.VIEW;
        state.lockedByUserCode = null;
        state.lockedByUserName = null;
        state.lockedDatetime = null;
        state.lockedByYou = false;
        state.lockedInDifferentBrowser = false;
        state.loading = keepLoading;
      })
      .addCase(cancelEdit.pending, (state) => {
        state.loading = true;
      })
      .addCase(cancelEdit.rejected, (state) => {
        state.loading = true;
      })
      .addCase(cancelEdit.fulfilled, (state) => {
        state.loading = false;
        state.matrixMode = MATRIX_MODES.EDIT;
        state.routeEdit = null;
        state.routesImpacted = {};
        state.routeConfigEdit = state.routeConfigEditCopy;
        state.routeConfigEditCopy = null;
        state.redirectConfig = state.redirectConfigCopy;
        state.redirectConfigCopy = null;
        state.editRoutes = state.editRoutesCopy;
        state.editRoutesCopy = null;
        state.editCollectionAreas = state.editCollectionAreasCopy;
        state.editCollectionAreasCopy = null;
        state.editOverrideAreas = state.editOverrideAreasCopy;
        state.editOverrideAreasCopy = null;
        state.editPickupAreas = state.editPickupAreasCopy;
        state.editPickupAreasCopy = null;
        state.editLocationConfig = state.editLocationConfigCopy;
        state.editLocationConfigCopy = null;
        state.routesTimes = state.routesTimesCopy;
        state.routesTimesCopy = null;
        state.routesByOuter = state.routesByOuterCopy;
        state.routesByOuterCopy = null;
      })
      .addCase(applyEdit.pending, (state) => {
        state.loading = true;
      })
      .addCase(applyEdit.rejected, (state) => {
        state.loading = true;
      })
      .addCase(applyEdit.fulfilled, (state, action) => {
        state.loading = false;
        state.matrixMode = MATRIX_MODES.EDIT;
        state.routesModified = action.payload.routesModified;
        state.routesModifiedCopy = {};
        state.routeEdit = null;
        state.routesImpacted = {};
        state.editRoutesCopy = null;
        state.editCollectionAreasCopy = null;
        state.editOverrideAreasCopy = null;
        state.editPickupAreasCopy = null;
        state.editLocationConfigCopy = null;
      })
      .addCase(optimiseRouteEditImpact.pending, (state) => {
        state.loading = true;
      })
      .addCase(optimiseRouteEditImpact.rejected, (state) => {
        state.loading = false;
      })
      .addCase(optimiseRouteEditImpact.fulfilled, (state, action) => {
        state.loading = false;
        state.editRoutes = action.payload.routes;
        state.routesImpacted = action.payload.routesImpacted;
        state.optimiseResultsOpen = true;
      })
      .addCase(savePlan.pending, (state) => {
        state.loading = true;
      })
      .addCase(savePlan.rejected, (state) => {
        state.loading = false;
      })
      .addCase(savePlan.fulfilled, (state, action) => {
        const { planValidated, matrixProfile } = action.payload;
        state.routeMatrix = {
          ...state.routeMatrix,
          matrixProfile: {
            ...matrixProfile,
            planValidated,
          },
        };
        if (state.editRoutes) state.routes = state.editRoutes;
        if (state.editCollectionAreas)
          state.collectionAreas = state.editCollectionAreas;
        if (state.editOverrideAreas)
          state.overrideAreas = state.editOverrideAreas;
        if (state.editPickupAreas) state.pickupAreas = state.editPickupAreas;
        if (state.editLocationConfig)
          state.locationConfig = state.editLocationConfig;
        state.matrixProfile = {
          ...state.matrixProfile,
          routeConfig: state.routeConfigEdit || state.matrixProfile.routeConfig,
          planValidated,
        };
        state.loading = false;
        state.matrixMode = MATRIX_MODES.VIEW;
        state.checksum = matrixProfile.checksum;
        state.lockedByUserCode = null;
        state.lockedByUserName = null;
        state.lockedDatetime = null;
        state.lockedByYou = false;
        state.lockedInDifferentBrowser = false;
        state.driverEdit = null;
        state.driversCopy = null;
        state.routeEdit = null;
        state.editRoutes = null;
        state.editRoutesCopy = null;
        state.editCollectionAreas = null;
        state.editCollectionAreasCopy = null;
        state.editOverrideAreas = null;
        state.editOverrideAreasCopy = null;
        state.editPickupAreas = null;
        state.editPickupAreasCopy = null;
        state.editLocationConfig = null;
        state.editLocationConfigCopy = null;
        state.routesModified = {};
        state.routesModifiedCopy = {};
      })
      .addCase(validateProfile.fulfilled, (state, action) => {
        const isValid = action.payload?.isValid;
        state.routeMatrix = {
          ...state.routeMatrix,
          matrixProfile: {
            ...state.routeMatrix.matrixProfile,
            planValidated: isValid,
          },
        };
      })
      .addCase(setWaveEditMode.pending, (state) => {
        state.loading = true;
      })
      .addCase(setWaveEditMode.rejected, (state) => {
        state.loading = false;
      })
      .addCase(setWaveEditMode.fulfilled, (state, action) => {
        const { waveEditMode, routes, routeConfigEdit, redirectConfig } =
          action.payload;
        state.waveEditMode = !!waveEditMode;
        state.editRoutes = routes;
        state.editRoutesCopy = waveEditMode ? routes : null;
        state.routeConfigEditCopy = waveEditMode ? routeConfigEdit : null;
        state.redirectConfigCopy = waveEditMode ? redirectConfig : null;
        state.depotRoutesOptimisedCopy = waveEditMode
          ? state.depotRoutesOptimised
          : false;
        state.loading = false;
      })
      .addCase(moveWave.pending, (state) => {
        state.loading = true;
      })
      .addCase(moveWave.rejected, (state) => {
        state.loading = false;
      })
      .addCase(moveWave.fulfilled, (state, action) => {
        state.loading = false;
        state.editRoutes = action.payload.editRoutes;
        state.waves = action.payload.waves;
        state.matrixMode = MATRIX_MODES.EDIT;
        state.routeConfigEdit = action.payload.routeConfigEdit;
        state.redirectConfig = action.payload.redirectConfig;
        state.volumeData = action.payload.volumeData;
        state.routesByOuter = action.payload.routesByOuter;
        state.routesTimes = action.payload.routesTimes;
        state.cutOffTimes = action.payload.cutOffTimes;
        state.depotRoutesOptimised = false;
      })
      .addCase(applyWavesChanges.fulfilled, (state, action) => {
        state.routeConfigEditCopy = null;
        state.depotRoutesOptimisedCopy = false;
        state.editRoutesCopy = null;
        state.waveEditMode = false;
        state.waves = action.payload.waves;
      })
      .addCase(cancelWavesChanges.pending, (state) => {
        state.loading = true;
      })
      .addCase(cancelWavesChanges.fulfilled, (state, action) => {
        const {
          routeConfigEdit,
          redirectConfig,
          depotRoutesOptimised,
          editRoutes,
          volumeData,
          routesByOuter,
          routesTimes,
          cutOffTimes,
        } = action.payload;
        state.routeConfigEdit = routeConfigEdit;
        state.routeConfigEditCopy = null;
        state.redirectConfig = redirectConfig;
        state.redirectConfigCopy = null;
        state.depotRoutesOptimised = depotRoutesOptimised;
        state.depotRoutesOptimisedCopy = false;
        state.editRoutes = editRoutes;
        state.editRoutesCopy = null;
        state.volumeData = volumeData;
        state.routesByOuter = routesByOuter;
        state.routesTimes = routesTimes;
        state.cutOffTimes = cutOffTimes;
        state.waves = getWavesFromRoutes(editRoutes, state.type);
        state.waveEditMode = false;
        state.loading = false;
      })
      .addCase(lockPlan.fulfilled, (state, action) => {
        state.lockedByUserCode = action.payload.lockedByUserCode;
        state.lockedByUserName = action.payload.lockedByUserName;
        state.lockedDatetime = action.payload.lockedDatetime;
        state.lockedByYou = true;
        state.lockedInDifferentBrowser = false;
      })
      .addCase(editCollectionTimes.fulfilled, (state, action) => {
        state.editCollectionAreas = action.payload;
      })
      .addCase(setMatrixModeThunk.fulfilled, (state, action) => {
        if (!Object.values(MATRIX_MODES).includes(action.payload)) return;
        state.matrixMode = action.payload;
      })
      .addCase(editPostcodesOverrides.fulfilled, (state, action) => {
        state.editModalOverrides = action.payload;
      })
      .addCase(generateModalNewOverrides.fulfilled, (state, action) => {
        state.editModalOverrides = {
          ...state.editModalOverrides,
          newOverrides: action.payload,
        };
      })
      .addCase(updateModalNewOverrides.fulfilled, (state, action) => {
        state.editModalOverrides = {
          ...state.editModalOverrides,
          newOverrides: action.payload,
        };
      })
      .addCase(applyOverridesEdit.pending, (state) => {
        state.loading = true;
      })
      .addCase(applyOverridesEdit.rejected, (state) => {
        state.loading = false;
      })
      .addCase(applyOverridesEdit.fulfilled, (state, action) => {
        state.editOverrideAreas = action.payload.overrideAreas;
        state.editRoutes = action.payload.routes;
        state.routesByOuter = action.payload.routesByOuter;
        state.routesTimes = action.payload.routesTimes;
        state.cutOffTimes = action.payload.cutOffTimes;
        state.volumeData = action.payload.volumeData;
        state.editModalOverrides = null;
        state.loading = false;
      })
      .addCase(cancelOverridesEdit.fulfilled, (state) => {
        state.editModalOverrides = null;
      })
      .addCase(editPostcodesPickup.fulfilled, (state, action) => {
        state.editModalPickupAreas = action.payload.pickupAreas;
        state.pickupModalConfig = action.payload.pickupModalConfig;
      })
      .addCase(updatePickupModal, (state, action) => {
        state.editModalPickupAreas = action.payload.pickupAreas;
        state.pickupModalConfig = action.payload.pickupModalConfig;
      })
      .addCase(applyPickupEdit.pending, (state) => {
        state.loading = true;
      })
      .addCase(applyPickupEdit.rejected, (state) => {
        state.loading = false;
      })
      .addCase(applyPickupEdit.fulfilled, (state, action) => {
        state.loading = false;
        state.routeEdit = action.payload.routeEdit;
        state.editRoutes = action.payload.routes;
        state.editPickupAreas = action.payload.pickupAreas;
        state.volumeData = action.payload.volumeData;
        state.editModalPickupAreas = null;
        state.pickupModalConfig = null;
        state.loading = false;
      })
      .addCase(cancelPickupEdit.fulfilled, (state) => {
        state.editModalPickupAreas = null;
        state.pickupModalConfig = null;
      });
  },
});

const { reducer } = matrixProfileSlice;
const {
  setRouteView,
  setSelectedOverlay,
  setHeatmapView,
  setCollectionView,
  setSelectedCollection,
  setMultiRoutesPopupLocation,
  setOptimiseResultsOpen,
  setMatrixMode,
  updateModalEditOverride,
  addModalDeleteOverride,
} = matrixProfileSlice.actions;
export {
  initialState,
  reducer,
  setRouteView,
  setSelectedOverlay,
  setHeatmapView,
  setCollectionView,
  setSelectedCollection,
  setMultiRoutesPopupLocation,
  setOptimiseResultsOpen,
  setMatrixMode,
  updateModalEditOverride,
  addModalDeleteOverride,
};
